import React, { useEffect, useState } from 'react';

import BookingConfirmationTable from './BookingConfirmationTable';
import './BookingConfirmation.css';
import { apiFetch } from '../../utils/apiClient';

const BookingConfirmation = ({ bookingGroupID }) => {

    // local development uses env.development, production routes to /api
    const API_URL = process.env.REACT_APP_API_URL || "/api";

    const [bookingData, setBookingData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');

    useEffect(() => {
        const fetchBookingData = async () => {
            try {

                const bookingsURL = `${API_URL}/bookings/${bookingGroupID}`;

                const response = await apiFetch(`${bookingsURL}`);

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(`${data.error}`);
                }

                setBookingData(data);

            } catch (error) {
                // catch any errors, i.e. ones not caught by the fetch call
                // and also the one thrown by the fetch call
                setApiErrorMessage(error.message);
                setIsErrorModalOpen(true);
            } finally {
                setLoading(false);
            }
        };

        fetchBookingData();
    }, [API_URL, bookingGroupID]);

    // ---------------------------------------------------------------
    // TODO UPDATE THIS WITH PROPER DISPLAYS ...
    if (loading) return <p>Loading booking details...</p>;
    // ---------------------------------------------------------------

    return (
        <div className='booking-confirmation-container'>
            {bookingData ? (
                <div>
                    <h2>You're all set!</h2>
                    <p>Your workspace at Room 49 is booked.</p>
                    <div className='info-block'>
                        <p>
                            {bookingData.first_name} {bookingData.last_name}
                        </p>
                        <p>
                            {bookingData.email}
                        </p>
                    </div>
                    <BookingConfirmationTable
                        finalDesks={bookingData.booking_dates}
                        totalPrice={bookingData.total_price}
                        bookingType={bookingData.booking_type}
                        discountCode={bookingData.discount_code}
                        discountPercentage={bookingData.discount_percentage}
                        discountAmount={bookingData.discount_amount}
                        originalPrice={bookingData.discount_amount ? (bookingData.total_price + bookingData.discount_amount).toFixed(0) : null} />
                    <div className='info-block'>
                        <p><strong>Finding the space</strong></p>
                        <p>You're looking for number <a href="https://maps.app.goo.gl/nK474YkiqczVyLSm9" target='_blank' rel='noopener noreferrer' >49 St Johns Road</a>, next to Phoenix Antiques, directly opposite Roots plant & gift shop across the road.</p>
                    </div>

                    <div className='info-block'>
                        {bookingData.udm_pincode ? (
                            <>
                                <p>
                                    <strong>Door: {bookingData.udm_pincode}</strong>
                                </p>
                                <p>
                                    The <a href="https://thetypingrooms.co.uk/entry">keypad</a> is mounted on the wall to the left of the door. Tap PIN, type your code and wait for a soft click. The door can then be pushed open. For monthly users, tap QR code and scan it from your email. A bright screen helps it register.
                                </p>
                            </>
                        ) : (
                            /* #230: the booking is paid and confirmed, but the door code has not
                               come through yet. Previously this rendered "Door:" with nothing
                               after it, which reads as though something is broken. The operator
                               is alerted automatically and the code follows by email, so this
                               says exactly that rather than leaving a blank. */
                            <>
                                <p>
                                    <strong>Door code: on its way</strong>
                                </p>
                                <p>
                                    Your booking is confirmed and your desk is reserved. We're just finishing setting up your door code &mdash; we'll email it to you shortly, and there's nothing you need to do. If it hasn't arrived by the morning of your booking, email <a href="mailto:help@thetypingrooms.co.uk">help@thetypingrooms.co.uk</a> and we'll sort it straight away.
                                </p>
                            </>
                        )}
                    </div>
                    <div className='info-block'>
                        <p><strong>Your desk</strong></p>
                        <p>Check the <a href="https://thetypingrooms.co.uk/images/room49-floorplan.jpg">floorplan</a> to find your desk number for the day. It'll be clearly marked with a large coloured number circle on the desk. Please sit only at the desk you booked. Each desk has sockets nearby for your laptop, phone or other kit. These are fine to use, but don't unplug anything already connected.</p>
                    </div>
                    <div className='info-block'>
                        <p>
                            <strong>Daily desks</strong>
                        </p>
                        <p>
                            Your personal code lets you come and go as you please 7am-7pm. The desk is solely yours for the duration of the day so you're welcome to leave your laptop or other bits there if you head out.
                        </p>
                    </div>
                    <div className='info-block'>
                        <p>
                            <strong>Monthly desks</strong>
                        </p>
                        <p>
                            Access works the same. Your number code or QR will work on the keypad Mon-Fri 7am-7pm. The desk is fully yours for the duration of your booking. It's expected to be the new permanent home for your full kit: monitor, CPU, keyboard, mouse, books—whatever you need to work properly. That's exactly how we use ours.
                        </p>
                    </div>
                    <div className='info-block'>
                        <p><strong>Meeting room</strong></p>
                        <p>
                            Now you've got your desk, you have the option to use the basement meeting room. Please visit the <a href="https://thetypingrooms.co.uk/meeting">meeting room</a> page to book.
                        </p>
                    </div>
                    <div className='info-block'>
                        <p>
                            <strong>Wi-Fi: MtjS8GSsMdXy</strong>
                        </p>
                        <p>
                            Connect to the Wi-Fi network named "room 49". Monthly desks can also plug in via ethernet. Just connect your cable and go.
                        </p>
                    </div>
                    <div className='info-block'>
                        <p><strong>Any issues</strong></p>
                        <p>
                            If you have issues with the door code, Wi-Fi, or anything that stops you using the space, email <a href="help@thetypingrooms.co.uk">help@thetypingrooms.co.uk</a>
                        </p>
                    </div>
                    <div className='info-block'>
                        <p>Your desk awaits...</p>
                        <h2>
                            Team 49
                        </h2>
                    </div>
                </div>
            ) : (
                <div>
                    <p>We are sorry but we could not find your booking confirmation.</p>
                </div>
            )
            }

            {
                isErrorModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <p>
                                Sorry, something went wrong and we couldn't fetch your booking confirmation. Please refresh the page or get in touch at <a href='help@thetypingrooms.co.uk'> help@thetypingrooms.co.uk</a>
                            </p>
                            <p className='error-code'>
                                {apiErrorMessage}
                            </p>
                            <button onClick={() => {
                                setIsErrorModalOpen(false);
                            }}>
                                Close
                            </button>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default BookingConfirmation;
